import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ImageAsset } from '@/components/common'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo } from '@/components/common/typography'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import { currencyText } from '@/utils/utilNumber'

interface PaymentInfoSectionProps {
  isMembership: boolean
  /** 총 상품 금액 */
  productOriginalAmount: number | undefined
  /** 총 배송비 (배송비 쿠폰 할인이 이미 차감된 값) */
  shippingAmount: number
  /** 배송비 쿠폰 할인 (총 배송비는 이 값을 되더해 쿠폰 적용 전 금액으로 표시하고, 할인은 별도 라인으로 노출) */
  shippingCouponDiscountAmount: number
  /** 할인 (상품 할인 + 상품 쿠폰 + 주문 쿠폰) */
  discountAmountWithoutMileage: number | undefined
  /** 상품 할인 */
  productPromotionCustomerDiscountAmount: number | undefined
  /** 상품 쿠폰 */
  productCouponDiscountAmount: number
  /** 주문 쿠폰 */
  orderCouponDiscountAmount: number
  /** 적립금 사용 */
  mileageDiscountAmount: number
  /** 최종 결제 금액 */
  amount: number
}

export const PaymentInfoSection = ({
  isMembership,
  shippingAmount,
  shippingCouponDiscountAmount,
  discountAmountWithoutMileage,
  productCouponDiscountAmount,
  orderCouponDiscountAmount,
  mileageDiscountAmount,
  amount,
  productPromotionCustomerDiscountAmount = 0,
  productOriginalAmount = 0,
}: PaymentInfoSectionProps) => {
  const { color } = useTheme()

  const [isOpen, setIsOpen] = useState<boolean>(true)

  /** 배송비 쿠폰 할인이 차감되기 전(기존)의 총 배송비. 쿠폰 할인은 별도 라인으로 노출한다. */
  const originalShippingAmount = shippingAmount + shippingCouponDiscountAmount

  /** 하단 "총 O원 할인 적용" 배지 금액. 상품/쿠폰 할인에 배송비 쿠폰 할인까지 합산한다. */
  const totalDiscountAmount = (discountAmountWithoutMileage ?? 0) + shippingCouponDiscountAmount

  const handleToggle = () => {
    setIsOpen((prevState) => !prevState)
  }

  // 0원일 경우에도 노출되는 항목
  // - 총 상품금액 (0원으로 표시)
  // - 총 배송비 (0원으로 표시) / 핏펫플러스일 경우 무료배송으로 표시
  // - 할인 (-0원으로 표시) > Red-500 타이포 컬러 유지
  // - 적립금 사용 (-0원으로 표시)
  // 0원일 경우에 노출되지 않는 항목
  // - 상품 할인
  // - 상품 쿠폰
  // - 주문 쿠폰
  return (
    <>
      <StyledSectionTitle onClick={handleToggle}>
        <Typo variant={'Body1 Bold'}>결제정보</Typo>
        <FlatIcon type={isOpen ? IconNameEnum.IcArrowUp : IconNameEnum.IcArrowDown} size={IconSizeEnum.Size24} />
      </StyledSectionTitle>
      {isOpen && (
        <StyledPaymentInfoWrapper>
          <StyledPaymentInfoDetail>
            <StyledPaymentInfoItem>
              <StyledPaymentInfoRowMain>
                <Typo variant="Body3 Regular">총 상품 금액</Typo>
                <Typo variant="Body3 Bold">{currencyText(productOriginalAmount)}</Typo>
              </StyledPaymentInfoRowMain>
              <StyledPaymentInfoRowMain>
                <Typo variant="Body3 Regular">총 배송비</Typo>
                {isMembership ? (
                  <StyledFreeShipping>
                    <ImageAsset name="fitpet_plus_logo" folder="membership" size={70} height={20} svg rem />
                    <Typo variant="Body3 Bold">무료배송</Typo>
                  </StyledFreeShipping>
                ) : (
                  <Typo variant="Body3 Bold">{currencyText(originalShippingAmount)}</Typo>
                )}
              </StyledPaymentInfoRowMain>
              {!isMembership && shippingCouponDiscountAmount > 0 && (
                <StyledPaymentInfoRowSub>
                  <Typo variant="Body4 Regular" color={color.gray400}>
                    배송비 쿠폰
                  </Typo>
                  <Typo variant="Body3 Regular" color={color.gray500}>
                    {`-${currencyText(shippingCouponDiscountAmount)}`}
                  </Typo>
                </StyledPaymentInfoRowSub>
              )}
              <StyledPaymentInfoRowMain>
                <Typo variant="Body3 Regular">할인</Typo>
                <Typo variant="Body3 Bold" color={color.red500}>
                  {`-${currencyText(discountAmountWithoutMileage || 0)}`}
                </Typo>
              </StyledPaymentInfoRowMain>
              {productPromotionCustomerDiscountAmount > 0 && (
                <StyledPaymentInfoRowSub>
                  <Typo variant="Body4 Regular" color={color.gray400}>
                    상품 할인
                  </Typo>
                  <Typo variant="Body3 Regular" color={color.gray500}>
                    {`-${currencyText(productPromotionCustomerDiscountAmount)}`}
                  </Typo>
                </StyledPaymentInfoRowSub>
              )}
              {productCouponDiscountAmount > 0 && (
                <StyledPaymentInfoRowSub>
                  <Typo variant="Body4 Regular" color={color.gray400}>
                    상품 쿠폰
                  </Typo>
                  <Typo variant="Body3 Regular" color={color.gray500}>
                    {`-${currencyText(productCouponDiscountAmount)}`}
                  </Typo>
                </StyledPaymentInfoRowSub>
              )}
              {orderCouponDiscountAmount > 0 && (
                <StyledPaymentInfoRowSub>
                  <Typo variant="Body4 Regular" color={color.gray400}>
                    주문 쿠폰
                  </Typo>
                  <Typo variant="Body3 Regular" color={color.gray500}>
                    {`-${currencyText(orderCouponDiscountAmount)}`}
                  </Typo>
                </StyledPaymentInfoRowSub>
              )}
            </StyledPaymentInfoItem>
            <StyledPaymentInfoItem>
              <StyledPaymentInfoRowMain>
                <Typo variant="Body3 Regular">적립금 사용</Typo>
                <Typo variant="Body3 Bold">{`-${currencyText(mileageDiscountAmount)}`}</Typo>
              </StyledPaymentInfoRowMain>
            </StyledPaymentInfoItem>
          </StyledPaymentInfoDetail>
          <StyledTotalPriceWrapper>
            <StyledTotalPriceRow>
              <Typo variant="Body1 Bold">최종 결제 금액</Typo>
              <Typo variant="Heading5 Bold">{currencyText(amount)}</Typo>
            </StyledTotalPriceRow>
            {totalDiscountAmount > 0 && (
              <StyledTotalDiscount>
                <Typo as="span" variant="Body3 Bold" color={color.red500}>
                  {`총 ${currencyText(totalDiscountAmount)}`} &nbsp;
                </Typo>
                <Typo as="span" variant="Body3 Bold">
                  할인 적용
                </Typo>
              </StyledTotalDiscount>
            )}
          </StyledTotalPriceWrapper>
        </StyledPaymentInfoWrapper>
      )}
    </>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${GlobalColorEnum.Gray70};
  cursor: pointer;
`

const StyledPaymentInfoWrapper = styled.div`
  padding: 1.6rem 1.6rem 2rem;
`

const StyledPaymentInfoDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${GlobalColorEnum.Gray70};
`

const StyledPaymentInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`

const StyledPaymentInfoRowMain = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledPaymentInfoRowSub = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 1rem;
`
const StyledTotalPriceWrapper = styled.div`
  padding-top: 2rem;
`

const StyledTotalPriceRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledTotalDiscount = styled.div`
  margin-top: 0.4rem;
  display: flex;
  margin-left: auto;
  width: fit-content;
  border: 1px solid ${GlobalColorEnum.Gray700};
  border-radius: 10rem;
  padding: 0.6rem 1rem;
`

const StyledFreeShipping = styled.div`
  display: flex;
  align-items: center;
`
